import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { api, isAuthPending } from '../lib/api';
import { useFavorites } from '../lib/favorites';
import EventReactionButtons from '../components/EventReactionButtons';
import EventDetailModal from '../components/EventDetailModal';
import EventReviewWarningChips from '../components/EventReviewWarningChips';
import { LoadingBlock } from '../components/Spinner';
import type { ExtractedEvent } from '../types';
import { formatRange } from '../lib/eventDates';

export default function EmailEventPage() {
  const { partitionKey = '', eventId = '' } = useParams();
  const [params, setParams] = useSearchParams();
  const action = params.get('action');
  const token = params.get('token');
  const { refresh } = useFavorites();
  const [event, setEvent] = useState<ExtractedEvent | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [showDetails, setShowDetails] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const processed = useRef(new Set<string>());
  const path = `/api/events/detail/${encodeURIComponent(partitionKey)}/${encodeURIComponent(eventId)}`;

  const downloadCalendar = useCallback(async () => {
    const content = await api.get<string>(`${path}/calendar`);
    const url = URL.createObjectURL(new Blob([content], { type: 'text/calendar;charset=utf-8' }));
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `event-${eventId}.ics`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 1000);
  }, [path, eventId]);

  useEffect(() => {
    let cancelled = false;
    const key = token || (action === 'calendar' ? `calendar:${partitionKey}:${eventId}` : '');
    setLoading(true);
    setError(null);
    void (async () => {
      try {
        const loaded = await api.get<ExtractedEvent>(path);
        if (cancelled) return;
        setEvent(loaded);
        if (action && action !== 'save' && action !== 'notInterested' && action !== 'calendar') {
          throw new Error('This email action is not supported. Use the event controls below.');
        }
        if ((action === 'save' || action === 'notInterested') && !token) {
          throw new Error('This email action link is incomplete. Use the event controls below.');
        }
        if (key && !processed.current.has(key)) {
          processed.current.add(key);
          if (action === 'calendar') {
            await downloadCalendar();
            if (!cancelled) setResult('Your calendar file is ready. Open it in your calendar app to finish adding the event.');
          } else if (action === 'save' || action === 'notInterested') {
            await api.post('/api/notifications/event-action', { token, partitionKey, eventId, action });
            await refresh();
            if (!cancelled) setResult(action === 'save'
              ? 'Saved. This event is bookmarked and helps improve your recommendations.'
              : 'Marked as not interested. We’ll show fewer events like this.');
          }
          if (!cancelled) {
            setLoading(false);
            setParams({}, { replace: true });
          }
        } else if (!action && !result) {
          setShowDetails(true);
        }
      } catch (err) {
        if (key) processed.current.delete(key);
        if (!cancelled && !isAuthPending(err)) {
          const message = err instanceof Error ? err.message : 'Could not open this event.';
          setError(message.includes('API 404') ? 'This event is no longer available. Browse the latest events instead.' : message);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [path, partitionKey, eventId, action, token, attempt, refresh, setParams, downloadCalendar]);

  async function downloadAgain() {
    setDownloading(true);
    setError(null);
    try {
      await downloadCalendar();
      setResult('Your calendar file is ready. Open it in your calendar app to finish adding the event.');
    } catch (err) {
      if (!isAuthPending(err)) setError(err instanceof Error ? err.message : 'Could not download the calendar file.');
    } finally {
      setDownloading(false);
    }
  }

  return (
    <section aria-labelledby="email-event-heading">
      <Link to="/events" className="d-inline-block mb-3">Back to events</Link>
      <h1 id="email-event-heading" className="h3">{event?.title || 'Opening event'}</h1>
      {loading ? <LoadingBlock label={action ? 'Applying your email action…' : 'Loading event…'} /> : null}
      {result ? <p className="alert alert-success" role="status">{result}</p> : null}
      {error ? (
        <div className="alert alert-danger" role="alert">
          <p>{error}</p>
          <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => setAttempt(n => n + 1)} disabled={loading}>
            Try again
          </button>
        </div>
      ) : null}
      {event ? (
        <>
          <p className="text-muted">
            {formatRange(event, { date: { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' } })}
            {event.venueName || event.locationText ? ` · ${event.venueName || event.locationText}` : ''}
          </p>
          <EventReviewWarningChips warnings={event.reviewWarnings} />
          <p className="mt-3">{event.aiSummary || event.description}</p>
          <div className="d-flex flex-wrap gap-2 align-items-center mt-3">
            <EventReactionButtons event={event} />
            <button type="button" className="btn btn-outline-primary" onClick={() => void downloadAgain()} disabled={downloading || loading}>
              {downloading ? 'Preparing calendar…' : 'Download calendar file'}
            </button>
            <button type="button" className="btn btn-outline-secondary" onClick={() => setShowDetails(true)}>View event details</button>
          </div>
          <EventDetailModal event={showDetails ? event : null} onClose={() => setShowDetails(false)} />
        </>
      ) : null}
    </section>
  );
}
